import { useCallback, useEffect, useMemo, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
  useGetCampaignSteps,
  useCreateCampaignStep,
  useUpdateCampaignStep,
  useDeleteCampaignStep,
  useReorderCampaignSteps,
  useGetCampaignById,
  useSaveSequenceFallback,
} from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import { useMainStore } from "@/stores/index";
import { showError } from "@/shared/utils/toast";

export const useCustomWorkflowBuilder = () => {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [insertAfterIndex, setInsertAfterIndex] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingStep, setDeletingStep] = useState(null);
  const [deletingCondition, setDeletingCondition] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [branchCondition, setBranchCondition] = useState(null);
  const campaignId = useCampaignId();

  const { data: campaignData } = useGetCampaignById(campaignId);
  const normalizedCampaign = useMemo(() => {
    if (campaignData == null) return null;
    const raw =
      campaignData?.data != null && typeof campaignData.data === "object"
        ? campaignData.data
        : campaignData;
    return raw;
  }, [campaignData]);
  const campaignStatus = String(normalizedCampaign?.status ?? "").toLowerCase();
  const isCampaignRunning =
    campaignStatus === "active" || campaignStatus === "running";

  const { data: steps, isLoading } = useGetCampaignSteps(
    campaignId,
    !!campaignId,
  );
  const { mutate: createStep, isPending: isCreatingStep } =
    useCreateCampaignStep();
  const { mutate: updateStep, isPending: isUpdating } = useUpdateCampaignStep();
  const { mutate: deleteStep, isPending: isDeletingStep } = useDeleteCampaignStep(
    {
      action: () => {
        setShowDeleteConfirmModal(false);
        setDeletingStep(null);
      },
    },
  );
  const { mutate: reorderSteps } = useReorderCampaignSteps();
  const { mutate: saveFallback } = useSaveSequenceFallback();
  const setWorkflowMode = useMainStore((state) => state.setWorkflowMode);


  useEffect(() => {
    if (steps && steps.length > 0) {
      const sortedSteps = [...steps].sort(
        (a, b) => a.step_order - b.step_order,
      );
      setItems(sortedSteps);
    } else {
      setItems([]);
    }
  }, [steps]);

  const handleDragStart = useCallback((event) => setActiveId(event.active.id), []);

  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          step_order: index + 1,
        }));

        setItems(updatedItems);

        try {
          reorderSteps({
            campaignId,
            stepsOrder: updatedItems.map((item) => ({
              id: item.id,
              order: item.step_order,
            })),
          });
        } catch (error) {
          console.error("Failed to reorder steps:", error);
          setItems(items);
        }
      }
    },
    [items, campaignId, reorderSteps],
  );

  const handleEdit = useCallback(({ item, variant = "step" }) => {
    setBranchCondition(null);
    if (variant === "condition") {
      setEditingCondition(item ?? null);
      setEditingStep(null);
    } else {
      setEditingStep(item);
      setEditingCondition(null);
    }
    setShowAddModal(true);
  }, []);

  const handleDelete = useCallback(({ item, variant = "step" }) => {
    setShowDeleteConfirmModal(true);
    if (variant === "condition") {
      setDeletingCondition(item);
    } else {
      setDeletingStep(item);
    }
  }, []);

  const handleSaveStep = useCallback(
    async (data) => {
      try {
        if (branchCondition) {
          createStep(
            {
              campaignId,
              stepData: {
                stepOrder: items.length + 1,
                ...data.step,
                conditions: null,
                delay: { days: 1 },
              },
            },
            {
              onSuccess: (createdStep) => {
                if (createdStep?.id) {
                  saveFallback({
                    fromStepId: branchCondition.id,
                    toStepId: createdStep.id,
                  });
                }
              },
            },
          );
          setShowAddModal(false);
          setEditingStep(null);
          setEditingCondition(null);
          setInsertAfterIndex(null);
          setBranchCondition(null);
        } else if (editingStep || editingCondition) {
          const base = editingCondition || editingStep;
          const isConditionEdit = !!editingCondition;
          const nextConditions = isConditionEdit
            ? data?.condition?.text
              ? JSON.stringify(data.condition.text)
              : base.conditions
            : base.conditions;

          updateStep({
            campaignId,
            stepId: base.id,
            stepData: {
              ...base,
              ...(editingStep && data?.step
                ? {
                    action: data.step.action ?? base.action,
                    channel: data.step.channel ?? base.channel,
                  }
                : {}),
              conditions: nextConditions,
            },
          });
          setShowAddModal(false);
          setEditingStep(null);
          setEditingCondition(null);
          setInsertAfterIndex(null);
          setBranchCondition(null);
        } else {
          createStep({
            campaignId,
            stepData: {
              stepOrder:
                insertAfterIndex !== null ? insertAfterIndex + 2 : items.length + 1,
              ...data.step,
              conditions: data.condition
                ? JSON.stringify(data.condition?.text)
                : null,
              delay: { days: 1 },
            },
          });
          setShowAddModal(false);
          setEditingStep(null);
          setEditingCondition(null);
          setInsertAfterIndex(null);
          setBranchCondition(null);
        }
      } catch (error) {
        setShowAddModal(false);
        setEditingStep(null);
        setEditingCondition(null);
        setInsertAfterIndex(null);
        setBranchCondition(null);
      }
    },
    [
      campaignId,
      editingStep,
      editingCondition,
      branchCondition,
      insertAfterIndex,
      items.length,
      createStep,
      updateStep,
      saveFallback,
    ],
  );

  const handleConfirmDelete = useCallback(() => {
    try {
      if (deletingCondition) {
        updateStep({
          campaignId,
          stepId: deletingCondition.id,
          stepData: { ...deletingCondition, conditions: null },
        });
      } else {
        deleteStep({ stepId: deletingStep?.id });
      }
    } catch (error) {
      console.error("Failed to delete step:", error);
    } finally {
      setShowDeleteConfirmModal(false);
      setDeletingStep(null);
      setDeletingCondition(null);
    }
  }, [campaignId, deleteStep, deletingStep, deletingCondition, updateStep]);

  const handleAddStep = useCallback(
    (insertAfter = null) => {
      if (isCampaignRunning) {
        showError(
          "You can't add a new step in a running campaign. Pause the campaign to make changes.",
        );
        return;
      }
      setBranchCondition(null);
      setInsertAfterIndex(insertAfter);
      setEditingStep(null);
      setShowAddModal(true);
    },
    [isCampaignRunning],
  );

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setEditingStep(null);
    setInsertAfterIndex(null);
    setEditingCondition(null);
    setBranchCondition(null);
  }, []);

  const handleAddBranchStep = useCallback(
    (condition) => {
      if (isCampaignRunning) {
        showError(
          "You can't add a branch step in a running campaign. Pause the campaign to make changes.",
        );
        return;
      }
      setBranchCondition(condition);
      setInsertAfterIndex(null);
      setEditingStep(null);
      setEditingCondition(null);
      setShowAddModal(true);
    },
    [isCampaignRunning],
  );

  const closeDeleteModal = useCallback(() => {
    setShowDeleteConfirmModal(false);
    setDeletingStep(null);
    setDeletingCondition(null);
  }, []);

  const activeItem = activeId ? items.find((item) => item.id === activeId) : null;


  const handleModeChange = useCallback((value) => {
    setWorkflowMode(value ? "smart" : "static");
  }, [setWorkflowMode]);

  return {
    items,
    steps,
    isLoading,
    activeItem,
    showAddModal,
    showDeleteConfirmModal,
    editingStep,
    editingCondition,
    deletingStep,
    deletingCondition,
    isCreatingStep,
    isUpdating,
    isDeletingStep,
    handleDragStart,
    handleDragEnd,
    handleEdit,
    handleDelete,
    handleSaveStep,
    handleConfirmDelete,
    handleAddStep,
    handleAddBranchStep,
    branchCondition,
    closeAddModal,
    closeDeleteModal,
    handleModeChange,
    isCampaignRunning,
  };
};
